import { getValidatedLimitParam, getValidatedPageParam, useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import { TParam, usePageParams } from 'hooks/use-page-params';
import { UseQueryOptions, UseQueryResult } from 'react-query';

const endpoint = 'ums/v1/cms/bnpl/credit-facilities';
export const getCreditFacilitiesEndpointIdentifier = endpoint;

export interface CreditFacilityDto {
  id: number;
  restaurantId: number;
  supplierId: number;
  facilityStatus: 'pending' | 'active' | 'suspended' | 'closed' | 'rejected';
  creditLimit: number;
  currencyCode: string;
  utilizedAmount: number;
  reservedAmount: number;
  availableAmount: number;
  balanceVersion: number;
  activatedAt: string | null;
  suspendedAt: string | null;
  closedAt: string | null;
  createdBy: number;
  createdAt: string;
  updatedAt: string;
  restaurant: { id: number; nameEn: string; nameAr: string };
  supplier: { id: number; name: string };
}

interface ResponseData {
  data: CreditFacilityDto[];
  count: number;
  total: number;
  page: number;
  pageCount: number;
  hasNext: boolean;
  hasPrev: boolean;
}

type TData = ResponseData;
type TError = AxiosError;
type TQueryKey = [typeof endpoint, { page: TParam; limit: TParam; status: TParam; supplierId: TParam; search: TParam }];
type Options = Omit<UseQueryOptions<unknown, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>;
type ReturnType = UseQueryResult<TData, TError>;

export function useGetCreditFacilitiesApi(options?: Options): ReturnType {
  const toast = useCustomToast();
  const page = getValidatedPageParam();
  const limit = getValidatedLimitParam();
  const isLoggedIn = useIsLoggedIn();
  const { getPageParams } = usePageParams();
  const { status, supplierId, search } = getPageParams();

  const queryFn = async () => {
    try {
      const { data } = await http.get(endpoint, {
        params: { page, limit, status, supplierId, search },
      });
      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;
      toast({ description: error.response.data.message, status: 'error' });
      throw error;
    }
  };

  return useQueryWrapper([endpoint, { page, limit, status, supplierId, search }], queryFn, {
    enabled: isLoggedIn,
    ...options,
  });
}
